import { useState } from 'react'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TRPCClientError, createTRPCClient, httpBatchLink } from '@trpc/client'
import { createTRPCContext } from '@trpc/tanstack-react-query'
import superjson from 'superjson'

import type { AppRouter } from '@server/routers'

export const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>()

const makeQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        retry: (failureCount, error) => {
          if (error instanceof TRPCClientError && ['UNAUTHORIZED', 'FORBIDDEN', 'NOT_FOUND'].includes(error.data?.code)) {
            return false
          }
          return failureCount < 2
        }
      }
    }
  })

let browserQueryClient: QueryClient | undefined

const getQueryClient = () => {
  if (typeof window === 'undefined') return makeQueryClient()
  if (!browserQueryClient) browserQueryClient = makeQueryClient()
  return browserQueryClient
}

export const Providers = ({ children }: { children: React.ReactNode }) => {
  const queryClient = getQueryClient()
  const [trpcClient] = useState(() =>
    createTRPCClient<AppRouter>({
      links: [httpBatchLink({ url: '/api/trpc', transformer: superjson })]
    })
  )

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
      </TRPCProvider>
    </QueryClientProvider>
  )
}

export const getErrorMessage = (error: unknown) => {
  if (error instanceof TRPCClientError) return error.message
  if (error instanceof Error) return error.message
  return 'Something went wrong, please try again'
}

export const getErrorCode = (error: unknown): string | undefined => {
  if (error instanceof TRPCClientError) return error.data?.orbitCode ?? error.data?.code
  return undefined
}
